'use client';

import { useState, useEffect } from 'react';
import FilterBar from './FilterBar';
import TestimonialCard from './TestimonialCard';
import VideoModal from './VideoModal';
import StatsOverview from './StatsOverview';
import CaseStudySpotlight from './CaseStudySpotlight';
import TrustIndicators from './TrustIndicators';
import Icon from '@/components/ui/AppIcon';

interface FilterState {
  strategy: string;
  timeframe: string;
  clientType: string;
  searchQuery: string;
}

interface Testimonial {
  id: number;
  clientName: string;
  clientTitle: string;
  clientImage: string;
  clientImageAlt: string;
  industry: string;
  strategy: string;
  clientType: string;
  testimonialText: string;
  videoThumbnail?: string;
  videoThumbnailAlt?: string;
  hasVideo: boolean;
  performanceMetrics: {
    roi: string;
    timeframe: string;
    portfolioGrowth: string;
  };
  verifiedBadge: boolean;
  dateAdded: string;
  videoUrl?: string;
  transcript?: string;
}

const ClientSuccessInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredTestimonials, setFilteredTestimonials] = useState<Testimonial[]>([]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{clientName: string;videoUrl: string;transcript?: string;} | null>(null);
  const [displayCount, setDisplayCount] = useState(6);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockStats = {
    totalClients: 247,
    averageROI: '+34.2%',
    clientRetention: '96.8%',
    referralRate: '78%'
  };

  const mockSpotlight = {
    id: 1,
    title: 'From Conservative to Confident: A Tech Executive\'s Journey',
    clientName: 'Sarah Chen',
    clientType: 'Tech Professional',
    industry: 'Software Engineering',
    challenge: 'Sarah had accumulated significant wealth through stock options but kept 80% in cash due to market anxiety. Her conservative approach was causing her to miss growth opportunities and lose purchasing power to inflation.',
    solution: 'We implemented a phased investment strategy starting with a balanced portfolio of 60% equities and 40% bonds, gradually increasing equity exposure as Sarah gained confidence. We focused on diversified index funds and dividend-paying stocks aligned with her risk tolerance.',
    results: [
    'Portfolio value increased from $2.1M to $3.4M in 18 months',
    'Achieved 28.7% annual return vs. 2.1% in savings accounts',
    'Reduced portfolio volatility by 35% through strategic diversification',
    'Built confidence to increase equity allocation to 75%',
    'Generated $47,000 in annual dividend income'],

    beforeMetrics: {
      portfolioValue: '$2.1M',
      annualReturn: '2.1%',
      riskLevel: 'Very Low'
    },
    afterMetrics: {
      portfolioValue: '$3.4M',
      annualReturn: '28.7%',
      riskLevel: 'Moderate'
    },
    timeframe: '18 Months',
    featuredImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c61a4c74-1763295402256.png",
    featuredImageAlt: 'Asian woman in business attire smiling confidently in modern office with city skyline view'
  };

  const mockTrustIndicators = {
    awards: [
    { name: 'Best Investment Firm 2025', year: '2025', icon: '🏆' },
    { name: 'Top Client Satisfaction', year: '2024', icon: '⭐' },
    { name: 'Excellence in Wealth Management', year: '2024', icon: '🎖️' }],

    certifications: [
    { name: 'SEC Registered Investment Advisor', issuer: 'U.S. Securities and Exchange Commission' },
    { name: 'FINRA Member Firm', issuer: 'Financial Industry Regulatory Authority' },
    { name: 'SIPC Protected', issuer: 'Securities Investor Protection Corporation' }],

    mediaFeatures: [
    { outlet: 'Wall Street Journal', title: 'Innovative Approach to Transparent Investing' },
    { outlet: 'Forbes', title: 'Top Investment Firms for High-Net-Worth Individuals' },
    { outlet: 'Bloomberg', title: 'Daily Deal Flow Transparency Trend' }]

  };

  const mockTestimonials: Testimonial[] = [
  {
    id: 1,
    clientName: 'Michael Rodriguez',
    clientTitle: 'Chief Technology Officer',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_17f4e6f32-1763299691321.png",
    clientImageAlt: 'Hispanic man with short dark hair in navy suit smiling professionally',
    industry: 'Technology',
    strategy: 'Growth Equity',
    clientType: 'Professional',
    testimonialText: 'Inversión Rápida transformed my investment approach. Their transparent daily updates and proven track record gave me the confidence to invest aggressively. My portfolio has grown 42% in just 14 months, far exceeding my expectations.',
    videoThumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1146295ca-1763299689724.png",
    videoThumbnailAlt: 'Professional man in office setting during video interview with city background',
    hasVideo: true,
    performanceMetrics: {
      roi: '+42%',
      timeframe: '14 Mo',
      portfolioGrowth: '$890K'
    },
    verifiedBadge: true,
    dateAdded: 'Jan 10, 2026',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'Working with Inversión Rápida has been transformative for my financial future. Their team took the time to understand my goals and risk tolerance, then crafted a strategy that has consistently outperformed the market. The daily transparency gives me peace of mind.'
  },
  {
    id: 2,
    clientName: 'Jennifer Park',
    clientTitle: 'Family Office Director',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a54d361e-1763294125034.png",
    clientImageAlt: 'Asian woman with long black hair in professional blazer smiling warmly',
    industry: 'Family Office',
    strategy: 'Balanced Portfolio',
    clientType: 'Family Office',
    testimonialText: 'Managing our family\'s wealth requires trust and transparency. Inversión Rápida delivers both. Their daily deal updates and performance metrics allow us to stay informed while their expertise drives consistent returns. Highly recommended.',
    hasVideo: false,
    performanceMetrics: {
      roi: '+31%',
      timeframe: '24 Mo',
      portfolioGrowth: '$2.4M'
    },
    verifiedBadge: true,
    dateAdded: 'Jan 8, 2026'
  },
  {
    id: 3,
    clientName: 'David Thompson',
    clientTitle: 'Serial Entrepreneur',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_167ba037e-1763294226742.png",
    clientImageAlt: 'Caucasian man with beard in casual business attire smiling confidently outdoors',
    industry: 'Entrepreneurship',
    strategy: 'Value Investing',
    clientType: 'Entrepreneur',
    testimonialText: 'As an entrepreneur, I understand the value of transparency and results. Inversión Rápida excels at both. Their value investing strategy has protected my wealth during market volatility while generating impressive returns. The daily insights are invaluable.',
    videoThumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f316546c-1764637600027.png",
    videoThumbnailAlt: 'Entrepreneur in modern workspace with laptop and business charts',
    hasVideo: true,
    performanceMetrics: {
      roi: '+38%',
      timeframe: '18 Mo',
      portfolioGrowth: '$1.2M'
    },
    verifiedBadge: true,
    dateAdded: 'Jan 6, 2026',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'Inversión Rápida understands entrepreneurs. They know we need flexibility and transparency. Their approach has allowed me to grow my wealth while maintaining liquidity for new business opportunities.'
  },
  {
    id: 4,
    clientName: 'Dr. Aisha Patel',
    clientTitle: 'Chief Medical Officer',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_167a02761-1763296312845.png",
    clientImageAlt: 'Indian woman doctor in white coat with stethoscope smiling professionally',
    industry: 'Healthcare',
    strategy: 'Dividend Income',
    clientType: 'Professional',
    testimonialText: 'Between my demanding medical career and family, I needed an investment partner I could trust completely. Inversión Rápida\'s dividend income strategy provides steady cash flow while growing my principal. Their transparency is refreshing in this industry.',
    hasVideo: false,
    performanceMetrics: {
      roi: '+27%',
      timeframe: '20 Mo',
      portfolioGrowth: '$680K'
    },
    verifiedBadge: true,
    dateAdded: 'Jan 4, 2026'
  },
  {
    id: 5,
    clientName: 'Robert Chen',
    clientTitle: 'Investment Advisor',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0203c69-1763294964268.png",
    clientImageAlt: 'Asian man in dark suit with tie smiling professionally in office setting',
    industry: 'Financial Services',
    strategy: 'Growth Equity',
    clientType: 'Professional',
    testimonialText: 'As a financial advisor myself, I\'m extremely selective about where I invest my personal wealth. Inversión Rápida\'s track record and transparent approach impressed me immediately. They\'ve exceeded my expectations consistently.',
    videoThumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8529aa1-1770346207412.png",
    videoThumbnailAlt: 'Financial advisor in modern office with charts and graphs on screens',
    hasVideo: true,
    performanceMetrics: {
      roi: '+45%',
      timeframe: '16 Mo',
      portfolioGrowth: '$1.5M'
    },
    verifiedBadge: true,
    dateAdded: 'Jan 2, 2026',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'I recommend Inversión Rápida to my own clients because I\'ve seen firsthand how their strategies perform. The daily transparency and consistent results speak for themselves.'
  },
  {
    id: 6,
    clientName: 'Emily Martinez',
    clientTitle: 'Tech Startup Founder',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14320f6d8-1763300092666.png",
    clientImageAlt: 'Young woman with long brown hair in casual business attire smiling in startup office',
    industry: 'Technology',
    strategy: 'Balanced Portfolio',
    clientType: 'Emerging HNW',
    testimonialText: 'After my startup exit, I needed sophisticated wealth management. Inversión Rápida educated me on investment strategies while delivering exceptional returns. Their balanced approach perfectly matches my risk tolerance and growth goals.',
    hasVideo: false,
    performanceMetrics: {
      roi: '+33%',
      timeframe: '12 Mo',
      portfolioGrowth: '$540K'
    },
    verifiedBadge: true,
    dateAdded: 'Dec 30, 2025'
  },
  {
    id: 7,
    clientName: 'James Wilson',
    clientTitle: 'Real Estate Developer',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1da91affa-1763293647230.png",
    clientImageAlt: 'Caucasian man with gray hair in business suit smiling confidently',
    industry: 'Real Estate',
    strategy: 'Value Investing',
    clientType: 'Entrepreneur',
    testimonialText: 'Inversión Rápida\'s value investing approach aligns perfectly with my real estate mindset. They find undervalued opportunities and hold for long-term gains. The daily performance updates keep me informed without overwhelming me.',
    hasVideo: false,
    performanceMetrics: {
      roi: '+29%',
      timeframe: '22 Mo',
      portfolioGrowth: '$980K'
    },
    verifiedBadge: true,
    dateAdded: 'Dec 28, 2025'
  },
  {
    id: 8,
    clientName: 'Lisa Anderson',
    clientTitle: 'Corporate Executive',
    clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_195d890e6-1763299011260.png",
    clientImageAlt: 'African American woman in professional attire smiling warmly in corporate office',
    industry: 'Finance',
    strategy: 'Dividend Income',
    clientType: 'Professional',
    testimonialText: 'The dividend income strategy has been perfect for my retirement planning. Inversión Rápida\'s team is responsive, knowledgeable, and genuinely invested in my success. The transparency they provide is unmatched in the industry.',
    videoThumbnail: "https://images.unsplash.com/photo-1712904116084-1824b4853c1d",
    videoThumbnailAlt: 'Professional woman in office environment during video conference',
    hasVideo: true,
    performanceMetrics: {
      roi: '+25%',
      timeframe: '26 Mo',
      portfolioGrowth: '$720K'
    },
    verifiedBadge: true,
    dateAdded: 'Dec 26, 2025',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'Inversión Rápida has given me confidence in my retirement planning. Their dividend strategy provides steady income while my portfolio continues to grow. I couldn\'t be happier with the results.'
  }];


  useEffect(() => {
    if (isHydrated) {
      setFilteredTestimonials(mockTestimonials);
    }
  }, [isHydrated]);

  const handleFilterChange = (filters: FilterState) => {
    if (!isHydrated) return;

    let filtered = [...mockTestimonials];

    if (filters.strategy !== 'all') {
      filtered = filtered.filter((t) => t.strategy.toLowerCase().includes(filters.strategy));
    }

    if (filters.clientType !== 'all') {
      filtered = filtered.filter((t) => t.clientType.toLowerCase().includes(filters.clientType));
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter((t) =>
      t.clientName.toLowerCase().includes(query) ||
      t.industry.toLowerCase().includes(query) ||
      t.testimonialText.toLowerCase().includes(query)
      );
    }

    setFilteredTestimonials(filtered);
    setDisplayCount(6);
  };

  const handleVideoPlay = (id: number) => {
    if (!isHydrated) return;

    const testimonial = mockTestimonials.find((t) => t.id === id);
    if (testimonial && testimonial.hasVideo) {
      setSelectedVideo({
        clientName: testimonial.clientName,
        videoUrl: testimonial.videoUrl || '',
        transcript: testimonial.transcript
      });
      setIsVideoModalOpen(true);
    }
  };

  const handleLoadMore = () => {
    if (!isHydrated) return;
    setDisplayCount((prev) => prev + 6);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full pt-24 pb-16 px-4 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) =>
              <div key={i} className="h-32 bg-muted rounded"></div>
              )}
            </div>
            <div className="h-96 bg-muted rounded"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full pt-24 pb-16 px-4 lg:px-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4">
            Client Success Gallery
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Real stories from real clients. Every testimonial is verified, every metric is accurate, and every success is celebrated. See how we\'ve helped investors like you achieve their financial goals.
          </p>
        </div>

        <StatsOverview stats={mockStats} />

        <CaseStudySpotlight spotlight={mockSpotlight} />

        <TrustIndicators indicators={mockTrustIndicators} />

        <FilterBar onFilterChange={handleFilterChange} />

        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading font-bold text-2xl text-foreground">
            All Success Stories ({filteredTestimonials.length})
          </h2>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="CheckBadgeIcon" size={16} variant="solid" className="text-success" />
            <span>All testimonials verified</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredTestimonials.slice(0, displayCount).map((testimonial) =>
          <TestimonialCard
            key={testimonial.id}
            testimonial={testimonial}
            onVideoPlay={handleVideoPlay} />

          )}
        </div>

        {displayCount < filteredTestimonials.length &&
        <div className="flex justify-center">
            <button
            onClick={handleLoadMore}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-md font-heading font-semibold text-sm hover:bg-opacity-90 transition-all duration-300 flex items-center space-x-2">

              <span>Load More Stories</span>
              <Icon name="ChevronDownIcon" size={16} variant="outline" />
            </button>
          </div>
        }

        {filteredTestimonials.length === 0 &&
        <div className="text-center py-16">
            <Icon name="MagnifyingGlassIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-xl text-foreground mb-2">
              No stories found
            </h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or search query
            </p>
          </div>
        }
      </div>

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoData={selectedVideo} />

    </div>);

};

export default ClientSuccessInteractive;